import { Icon } from '@zoom/components';
import classnames from 'classnames';
import React from 'react';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';

type Props = {
  activeTimezone?: string;
  alignment: 'left' | 'right';
  onTimezoneChange: (tz: string) => void;
} & React.PropsWithChildren;

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const DropdownTimezoneContext = React.createContext<Props>(undefined!); // No need for default value

DropdownTimezoneContext.displayName = 'DropdownTimezoneContext';

/**
 * Example Usage:
 *
 * ```
 * <DropdownTimezone activeTimezone={timezone} alignment="right" onTimezoneChange={setTimezone}>
 *   <DropdownTimezoneItem tz="Europe/Bratislava">
 *     My Time Zone
 *   </DropdownTimezoneItem>
 *   <DropdownTimezoneItem subtitle="GMT +2" tz="Australia/Perth">
 *     Queue's Time Zone
 *   </DropdownTimezoneItem>
 *   <DropdownTimezoneSubmenu items={['Europe/Bratislava', 'Australia/Perth']}>
 *     <DropdownTimezoneItem subtitle="All available timezones">All Time Zones</DropdownTimezoneItem>
 *   </DropdownTimezoneSubmenu>
 * </DropdownTimezone>
 * ```
 */
export const DropdownTimezone: React.FC<Props> = ({
  activeTimezone,
  alignment = 'left',
  children,
  onTimezoneChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleTimezoneChange = (value: string) => {
    onTimezoneChange(value);
    setIsOpen(false);
  };

  const toggle = () => setIsOpen((open) => !open);

  return (
    <Dropdown className="dropdown-timezone" direction="down" isOpen={isOpen} toggle={toggle}>
      <DropdownToggle
        className={classnames('dropdown-timezone__toggle', { active: isOpen })}
        color="info"
        data-test-id="dropdown-timezone-toggle"
      >
        <Icon color="#00a8e2" kind="timezone" size={20} />
      </DropdownToggle>

      <DropdownTimezoneContext.Provider
        value={{
          activeTimezone,
          alignment,
          onTimezoneChange: handleTimezoneChange,
        }}
      >
        <DropdownMenu right={alignment === 'right'}>{children}</DropdownMenu>
      </DropdownTimezoneContext.Provider>
    </Dropdown>
  );
};
